import RemoveChipModal from '@/appview/ListChips/RemoveChipModal';
import { ContactInfoFormValues } from '@/appview/RegisterChipV2/ContactInfoStepV2/ContactInfoForm';
import { useEffect, useMemo, useState } from 'react';
import { lbsToKg } from '../../../lib/utils';
import { gqlTypes } from '../../../types/gqlTypes';
import { useUpdatePet } from '../../lib/mutations';
import { manageHomeQuery } from '../../ManageHome/ManageHome.graphql';
import ContactInfoForm from '../forms/ContactInfoForm/ContactInfoForm';
import PetInfoForm, { petFormValuesFromPet, PetInfo } from '../forms/PetInfoForm/PetInfoForm';
import styles from './EditPetAndContactInfo.module.scss';

export interface EditPetAndContactInfoProps {
  chipId: string;
  pet: gqlTypes.basePet | undefined;
  initialContactInfo?: gqlTypes.contactInfo;
  onDone(petOwnershipWasTransferred: boolean): void;
}

function formValuesFromGql(gql: gqlTypes.contactInfo) {
  const values: Partial<ContactInfoFormValues> = {
    // The only required fields.
    firstName: gql.firstName.trim(),
    primaryEmail: gql.primaryEmail,
  };
  if (gql.primaryPhone) {
    values.primaryPhone = gql.primaryPhone;
  }
  if (gql.lastName) {
    values.lastName = gql.lastName.trim();
  }
  if (gql.secondaryEmail) {
    values.secondaryEmail = gql.secondaryEmail;
  }
  if (gql.secondaryPhone) {
    values.secondaryPhone = gql.secondaryPhone;
  }
  if (gql.line1) {
    values.line1 = gql.line1;
  }
  if (gql.line2) {
    values.line2 = gql.line2;
  }
  if (gql.city) {
    values.city = gql.city;
  }
  if (gql.zipcode) {
    values.zipcode = gql.zipcode;
  }
  if (gql.state) {
    values.state = gql.state;
  }
  return values;
}

/**
 * Given the birthday parts from the form and the current pet DOB parts,
 * returns a DobUpdate with changed parts populated.
 * Returns undefined if nothing changed.
 */
type Dob = { dayOfBirth?: number | null; monthOfBirth?: number | null; yearOfBirth?: number | null };
type DobUpdate = Partial<Dob>;
function buildDobUpdate(formValues: PetInfo, current: Dob): DobUpdate | undefined {
  if (!formValues.birthMonth || !formValues.birthYear) {
    return;
  }

  const next: Dob = {
    dayOfBirth: formValues.birthDay,
    monthOfBirth: formValues.birthMonth,
    yearOfBirth: formValues.birthYear,
  };

  const diffs: DobUpdate = {};
  (['dayOfBirth', 'monthOfBirth', 'yearOfBirth'] as const).forEach((k) => {
    if (next[k] !== current[k]) diffs[k] = next[k];
  });

  if (Object.keys(diffs).length === 0) {
    return;
  }

  // Ensure all parts are present by backfilling from current
  // Use 'in' check to preserve explicit null values for dayOfBirth
  return {
    dayOfBirth: 'dayOfBirth' in diffs ? diffs.dayOfBirth : current.dayOfBirth,
    monthOfBirth: diffs.monthOfBirth ?? current.monthOfBirth,
    yearOfBirth: diffs.yearOfBirth ?? current.yearOfBirth,
  };
}

export default function EditPetAndContactInfo({ chipId, initialContactInfo, pet, onDone }: EditPetAndContactInfoProps) {
  const [updatePet] = useUpdatePet();
  const [updatePetError, setUpdatePetError] = useState<string | null>(null);

  const [updatePetInput, setUpdatePetInput] = useState<gqlTypes.UpdatePetInput | null>(null);

  const [petFormValues, setPetFormValues] = useState<PetInfo | null>(null);
  const [petFormIsValid, setPetFormIsValid] = useState(true);
  const petFormInitialValues = useMemo(() => pet && petFormValuesFromPet(pet), [pet]);

  useEffect(() => {
    if (!pet || !petFormValues) {
      return;
    }

    const newInput: gqlTypes.UpdatePetInput = {
      id: pet.id,
    };
    const values = petFormValues;

    if (values.name !== pet.name) {
      newInput.name = values.name;
    }

    if (!petFormValues.birthMonth || !petFormValues.birthYear) {
      return;
    }

    const birthDateUpdates = buildDobUpdate(petFormValues, {
      dayOfBirth: pet.dayOfBirth,
      monthOfBirth: pet.monthOfBirth,
      yearOfBirth: pet.yearOfBirth,
    });

    if (birthDateUpdates) {
      Object.assign(newInput, birthDateUpdates);
    }

    if (values.weight !== petFormInitialValues?.weight) {
      const newWeight = lbsToKg(Number(values.weight));
      newInput.weight = newWeight;
    }

    if (values.breedId !== pet.breed?.id) {
      newInput.breedId = values.breedId;
    }

    if (values.gender !== pet.gender) {
      newInput.gender = values.gender;
    }

    if (values.species !== pet.species) {
      newInput.species = values.species;
    }

    const valuesIsPurebred = !values.isMixedBreed;
    if (valuesIsPurebred !== pet.isPurebred) {
      newInput.isPurebred = valuesIsPurebred;
    }

    if (values.color !== pet.color) {
      newInput.color = values.color;
    }

    if (values.additionalInfo !== pet.additionalInfo) {
      newInput.additionalInfo = values.additionalInfo;
    }

    const inputIfChanged = Object.keys(newInput).filter((key) => key !== 'id').length > 0 ? newInput : null;

    setUpdatePetInput(inputIfChanged);
  }, [pet, petFormValues, petFormInitialValues]);

  const initialContactInfoFormValues = useMemo(
    () => (initialContactInfo ? formValuesFromGql(initialContactInfo) : undefined),
    [initialContactInfo],
  );

  return (
    <div className={styles.editFormContainer}>
      {pet && (
        <div>
          <div className={styles.header}>
            <h4>Pet Information</h4>
            <RemoveChipModal chipShortId={chipId} />
          </div>
          <PetInfoForm
            initialValues={petFormInitialValues}
            onValuesChanged={setPetFormValues}
            onIsValidChanged={setPetFormIsValid}
          />
        </div>
      )}
      <div>
        {pet && <h4>Contact Information</h4>}
        <ContactInfoForm
          initialValues={initialContactInfoFormValues}
          chipShortId={chipId}
          disabled={!petFormIsValid}
          submitLabel="Save"
          mutationOptions={{ refetchQueries: [{ query: manageHomeQuery }] }}
          allowPrimaryEmailEdit={true}
          responseError={updatePetError ?? undefined}
          preSubmit={
            updatePetInput
              ? () => {
                  // Updating the pet needs to happen before submitting contact info in case they are submitting the
                  // contact info with a change in primary email, which transfers the pet away from themselves. After
                  // the transfer happens, they would no longer have permission to edit the pet.
                  setUpdatePetError(null);
                  return updatePet({
                    variables: {
                      input: updatePetInput,
                    },
                    refetchQueries: [{ query: manageHomeQuery }],
                  }).catch((error) => {
                    setUpdatePetError(error.message || 'Failed to update pet information');
                    throw error;
                  });
                }
              : undefined
          }
          onSuccess={async (contactInfoInput) => {
            const petOwnershipWasTransferred = !!contactInfoInput?.updateChipContactInfo.petOwnershipWasTransferred;

            onDone(petOwnershipWasTransferred);
          }}
        />
      </div>
    </div>
  );
}
